import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, forkJoin, tap } from 'rxjs';
import { PLATFORM_CONFIG } from '../tokens';
import { CountryResponse } from '../models/platform';

@Injectable({
  providedIn: 'root'
})
export class PlatformService {
  protected readonly config = inject(PLATFORM_CONFIG);
  protected readonly http = inject(HttpClient);

  public countries = signal<CountryResponse[]>([]);
  public timezones = signal<any[]>([]);
  public platformParams = signal<any | null>(null);

  public selectedCountry = signal<CountryResponse | null>(null);
  public selectedTimeZone = signal<any | null>(null);

  constructor() {}

  async init() {
    const [countries] = await firstValueFrom(
      forkJoin([this.getCountries(), this.getTimezones(), this.getParams()])
    );

    this.setInitialLocation(countries);

    return countries;
  }

  private setInitialLocation(countries: CountryResponse[]) {
    if (!countries.length) return;

    const browserCode = this.getBrowserLocale();
    console.log('browserCode', browserCode);
    let country = countries.find((c) => c.current);
    if (!country) {
      country = countries.find((c) => c.code === browserCode) || countries[0];
    }
    this.selectedCountry.set(country);

    const browserTZ = this.getBrowserTimeZone();
    const tz = country.time_zones?.find((t: any) => t.id === browserTZ) ||
      country.time_zones?.[0] || { id: browserTZ, title: browserTZ };

    this.selectedTimeZone.set(tz);
  }

  getParams() {
    return this.http
      .get(`${this.config.platformUrl}/settings/platform/params`)
      .pipe(tap((res) => this.platformParams.set(res)));
  }

  getCountries() {
    return this.http
      .get<CountryResponse[]>(`${this.config.platformUrl}/settings/countries`)
      .pipe(tap((res) => this.countries.set(res)));
  }

  getTimezones() {
    return this.http
      .get<any[]>(`${this.config.platformUrl}/settings/timezones`)
      .pipe(tap((res) => this.timezones.set(res)));
  }

  private getBrowserTimeZone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  private getBrowserLocale(): string {
    const locale = navigator.language || (navigator as any).userLanguage;
    const parts = locale.split('-');
    return (parts.length > 1 ? parts[1] : parts[0]).toUpperCase();
  }
}
